import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Container,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { useTranslation } from 'react-i18next';
import { getCustomerDiscounts, CustomerDiscount } from '../api/customerDiscounts';
import CouponDetailsDialog from '../components/coupons/CouponDetailsDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`coupons-tabpanel-${index}`}
      aria-labelledby={`coupons-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const MyCoupons: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [discounts, setDiscounts] = useState<CustomerDiscount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<CustomerDiscount | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const response = await getCustomerDiscounts();
      setDiscounts(response.discounts);
    } catch (err) {
      setError('Failed to fetch discounts');
      console.error('Error fetching discounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCouponClick = (coupon: CustomerDiscount) => {
    setSelectedCoupon(coupon);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCoupon(null);
  };





  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ 
        bgcolor: 'white', 
        borderBottom: '1px solid #e0e0e0',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            pt: 2,
            gap: 1
          }}>
            <IconButton onClick={handleBack} sx={{ color: '#B3907A' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography 
              sx={{ 
                color: '#B3907A', 
                fontWeight: 600,
                fontSize: '16px',
              }}
            >
              {t('coupons.myCoupons')}
            </Typography>
          </Box>

          {/* Tabs */}
          <Box>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: '#9CA3AF',
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 3,
                },
                '& .Mui-selected': {
                  color: '#B3907A',
                  fontWeight: 600,
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab label={t('coupons.notClaimed')} />
              <Tab label={t('coupons.claimed')} />
              <Tab label={t('coupons.history')} />
            </Tabs>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <TabPanel value={tabValue} index={0}>
          {/* Not Claimed Tab */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {discounts
              .filter(discount => discount.canUse && !discount.isExpired)
              .map((discount) => (
                <Card 
                  key={discount.id}
                  onClick={() => handleCouponClick(discount)}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      transition: 'box-shadow 0.3s ease'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Icon */}
                      <Box sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#B3907A',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <CardGiftcardIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#B3907A', 
                            fontWeight: 700,
                            fontSize: '18px',
                            mb: 0.5
                          }}
                        >
                          {discount.code}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#B3907A',
                            fontSize: '14px'
                          }}
                        >
                          {discount.name}
                        </Typography>

                        {/* Category Chip */}
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={discount.category.replace(/_/g, ' ').toUpperCase()}
                            size="small"
                            sx={{
                              bgcolor: '#B3907A',
                              color: 'white',
                              fontSize: '10px',
                              height: 20,
                              '& .MuiChip-label': {
                                px: 1,
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Claimed Tab */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('coupons.noClaimedCoupons')}
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* History Tab */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {t('coupons.noHistory')}
            </Typography>
          </Box>
        </TabPanel>
      </Container>

      {/* Coupon Details Dialog */}
      <CouponDetailsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        coupon={selectedCoupon}
      />
    </Box>
  );
};

export default MyCoupons;
